import { useCallback, useState } from 'react';

/**
 * Hand a downloaded photo to the visitor in the way their device can find it again.
 *
 * On iPhone and iPad a normal download lands in Files → Downloads, which people rarely
 * find. There we open the share sheet with the file attached instead: its "Save Image"
 * puts the photo in Photos. iOS only allows the share sheet straight after a tap, so when
 * fetching the photo took longer than that, a "Save to Photos" button asks for one more.
 *
 * Android saves downloads where Photos and Gallery apps pick them up, and computers
 * expect a download, so both keep the normal download.
 *
 * savePhoto resolves to "shared" (a share target completed — iOS doesn't say which),
 * "cancelled" (they closed the sheet or the prompt) or "downloaded".
 */

const MIME_BY_EXTENSION = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', heic: 'image/heic', gif: 'image/gif',
};

export const isAppleMobile = () => {
  try {
    const ua = navigator.userAgent;
    // iPadOS reports itself as a Mac; touch support gives it away.
    return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  } catch {
    return false;
  }
};

/** The filename the server sent (e.g. a .png social cut-out), else the fallback. */
export const filenameFromResponse = (response, fallback) => {
  const disposition = response?.headers?.['content-disposition'] || '';
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1]) : fallback;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

const shareFiles = async (files) => {
  try {
    await navigator.share({ files });
    return 'shared';
  } catch (error) {
    if (error.name === 'AbortError') return 'cancelled';
    throw error;
  }
};

const useSavePhoto = () => {
  const [pending, setPending] = useState(null); // { blob, filename, file, resolve }

  const savePhoto = useCallback(async (blob, filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    // Originals can arrive as application/octet-stream; iOS only offers "Save Image" for images.
    const type = MIME_BY_EXTENSION[extension] || (blob.type?.startsWith('image/') ? blob.type : 'image/jpeg');
    const file = new File([blob], filename, { type });

    if (isAppleMobile() && navigator.canShare?.({ files: [file] })) {
      try {
        return await shareFiles([file]);
      } catch (error) {
        if (error.name === 'NotAllowedError') {
          return new Promise((resolve) => setPending({ blob, filename, file, resolve }));
        }
        // Anything else: fall back to a normal download below.
      }
    }
    downloadBlob(blob, filename);
    return 'downloaded';
  }, []);

  const saveFromPrompt = async () => {
    const { blob, filename, file, resolve } = pending;
    setPending(null);
    try {
      // Called synchronously inside this tap, which is what iOS requires.
      resolve(await shareFiles([file]));
    } catch {
      downloadBlob(blob, filename);
      resolve('downloaded');
    }
  };

  const cancelPrompt = () => {
    pending.resolve('cancelled');
    setPending(null);
  };

  const savePrompt = pending ? (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-photo-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 10050, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 16,
      }}
      onClick={cancelPrompt}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 420, background: '#141414', color: '#fff', borderRadius: 18,
          padding: '22px 20px 16px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', marginBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div id="save-photo-title" style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Your photo is ready</div>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(255,255,255,0.72)', margin: '0 0 18px' }}>
          Tap below, then choose <strong style={{ color: '#fff' }}>Save Image</strong> to add it to your Photos.
        </p>
        <button
          type="button"
          onClick={saveFromPrompt}
          style={{
            width: '100%', padding: '14px 16px', borderRadius: 12, border: 'none', background: '#BF212F',
            color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Save to Photos
        </button>
        <button
          type="button"
          onClick={cancelPrompt}
          style={{
            width: '100%', padding: '12px 16px', marginTop: 8, borderRadius: 12, border: 'none', background: 'transparent',
            color: 'rgba(255,255,255,0.6)', fontSize: 15, cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  ) : null;

  return { savePhoto, savePrompt };
};

export default useSavePhoto;
